import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './GameDetailModal.module.css';

const PREVIEW_COUNT = 5;

const formatLogDate = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const resultClass = (result, type) => {
  if (result == null) return styles.logNeutral;
  if (type === 'ou') return styles.logOuResult;
  if (result === 'win' || result === 'over') return styles.logWin;
  if (result === 'loss' || result === 'under') return styles.logLoss;
  return styles.logNeutral;
};

const formatWlResult = (result) => {
  if (result === 'win') return 'WIN';
  if (result === 'loss') return 'LOSS';
  if (result === 'push') return 'PUSH';
  return '—';
};

const formatAtsResult = (result) => {
  if (result === 'win') return 'WIN';
  if (result === 'loss') return 'LOSS';
  if (result === 'push') return 'PUSH';
  return '—';
};

const formatOuResult = (result) => {
  if (result === 'over') return 'OVER';
  if (result === 'under') return 'UNDER';
  if (result === 'push') return 'PUSH';
  return '—';
};

const formatSummaryLine = (summary, label) => {
  if (!summary) return null;
  const wl = summary.wl;
  const ats = summary.ats;
  const ou = summary.ou;
  return (
    <>
      {label} W-L {wl.wins}-{wl.losses}
      {wl.pushes > 0 && `-${wl.pushes}`}
      {' • '}
      ATS {ats.wins}-{ats.losses}
      {ats.pushes > 0 && `-${ats.pushes}`}
      {' • '}
      O/U {ou.overs}-{ou.unders}
      {ou.pushes > 0 && `-${ou.pushes}`}
    </>
  );
};

const GameDetailGameLog = ({ game, matchupStats, loading, error }) => {
  const [activeTeam, setActiveTeam] = useState('away');
  const [expanded, setExpanded] = useState(false);

  const teamOptions = useMemo(() => ([
    {
      key: 'away',
      label: game.away_team.abbreviation || game.away_team.name,
      data: matchupStats?.awayTeam,
    },
    {
      key: 'home',
      label: game.home_team.abbreviation || game.home_team.name,
      data: matchupStats?.homeTeam,
    },
  ]), [game, matchupStats]);

  useEffect(() => {
    setExpanded(false);
  }, [activeTeam, game?.id]);

  const activeData = teamOptions.find((option) => option.key === activeTeam)?.data;
  const allEntries = activeData?.seasonLog?.entries || [];
  const hasMore = allEntries.length > PREVIEW_COUNT;
  const visibleEntries = expanded ? allEntries : allEntries.slice(0, PREVIEW_COUNT);
  const summary = expanded
    ? activeData?.seasonLog?.summary
    : (activeData?.seasonLog?.previewSummary ?? activeData?.seasonLog?.summary);
  const summaryLabel = expanded
    ? `Season (${allEntries.length} games):`
    : `Last ${Math.min(PREVIEW_COUNT, allEntries.length)}:`;

  return (
    <div className={styles.gameLogSection}>
      <div className={styles.gameLogHeader}>
        <div className={styles.teamToggle} role="tablist" aria-label="Select team game log">
          {teamOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={activeTeam === option.key}
              className={`${styles.teamToggleButton} ${activeTeam === option.key ? styles.teamToggleActive : ''}`}
              onClick={() => setActiveTeam(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className={styles.gameLogMessage}>Loading recent games…</p>}
      {!loading && error && <p className={styles.gameLogMessage}>{error}</p>}

      {!loading && !error && allEntries.length === 0 && (
        <p className={styles.gameLogMessage}>No completed games with lines yet this season.</p>
      )}

      {!loading && !error && allEntries.length > 0 && (
        <>
          {summary && (
            <div className={styles.gameLogSummary}>
              {formatSummaryLine(summary, summaryLabel)}
            </div>
          )}

          <div className={styles.gameLogTableWrap}>
            <table className={styles.gameLogTable}>
              <thead>
                <tr>
                  <th>Opponent</th>
                  <th>W/L</th>
                  <th>ATS</th>
                  <th>O/U</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => (
                  <tr key={entry.gameId}>
                    <td className={styles.logOpponentCell}>
                      <span className={styles.logOpponent}>
                        {entry.isHome ? '' : '@'}{entry.opponent}
                      </span>
                      <span className={styles.logDate}>{formatLogDate(entry.startDate)}</span>
                    </td>
                    <td className={styles.logResultCell}>
                      <span className={styles.logScore}>{entry.score}</span>
                      <span className={resultClass(entry.wl, 'wl')}>{formatWlResult(entry.wl)}</span>
                    </td>
                    <td className={styles.logResultCell}>
                      <span className={styles.logLine}>{entry.spreadLine || '—'}</span>
                      <span className={resultClass(entry.ats, 'ats')}>{formatAtsResult(entry.ats)}</span>
                    </td>
                    <td className={styles.logResultCell}>
                      <span className={styles.logLine}>{entry.totalLine || '—'}</span>
                      <span className={resultClass(entry.ou, 'ou')}>{formatOuResult(entry.ou)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && !expanded && (
            <button
              type="button"
              className={styles.gameLogShowMore}
              onClick={() => setExpanded(true)}
            >
              Show More
            </button>
          )}
        </>
      )}
    </div>
  );
};

GameDetailGameLog.propTypes = {
  game: PropTypes.object.isRequired,
  matchupStats: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default GameDetailGameLog;
